import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import React from "react";
import { useTranslation } from "react-i18next";

interface NextRewardCardProps {
  nextReward?: {
    withdrawnValue?: string;
    targetValue?: number;
    progress?: number;
    currentLevelImage?: string;
    isMaxLevelReached?: boolean;
  };
  totalAmount: string;
  currency: string;
}

function NextRewardCard({
  nextReward,
  totalAmount,
  currency,
}: NextRewardCardProps) {
  const { t, i18n } = useTranslation('dashboard');

  const formatCurrency = (value?: number) => {
    if (!value) return 'R$ 0,00';

    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency ?? 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const safeNextReward = {
    withdrawnValue: nextReward?.withdrawnValue ?? 0,
    targetValue: nextReward?.targetValue ?? 0,
    progress: nextReward?.progress ?? 0,
    currentLevelImage: nextReward?.currentLevelImage ?? '/placa2k.svg',
    isMaxLevelReached: nextReward?.isMaxLevelReached ?? false,
  };

  const isCompleted =
    safeNextReward.isMaxLevelReached && safeNextReward.progress === 100;

  return (
    <Card className='relative md:max-w-[328px] w-full h-[435px] md:h-[414px] overflow-hidden bg-zinc-950/95 md:backdrop-blur-sm border-zinc-800 rounded-lg flex-grow'>
      {isCompleted && (
        <div className='absolute inset-0 z-20 bg-gradient-to-r from-yellow-400/20 via-gold-500/20 to-yellow-600/20 animate-pulse' />
      )}

      <div className='absolute inset-0 z-0'>
        <img
          src={safeNextReward.currentLevelImage}
          alt={t('nextReward.rewardLevelAlt')}
          className={`w-full h-full object-cover ${
            isCompleted ? 'brightness-110 saturate-150' : ''
          }`}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = '/placa2k.svg';
          }}
        />
      </div>

      <div className='relative z-10 flex flex-col h-full'>
        <div className='flex items-center justify-between p-4 border-b border-zinc-800 bg-zinc-950/90'>
          <h3 className='text-xl font-semibold text-white'>
            {isCompleted
              ? t('nextReward.maxLevelTitle')
              : t('nextReward.title')}
          </h3>
        </div>

        <div className='flex-1 p-4'>
          <div className='space-y-4'>
            <div className='flex justify-between items-center'>
              <span className='text-2xl font-bold text-white'>
                {totalAmount}
              </span>

              <div className='flex items-center justify-center w-16 h-16'>
                <div className='relative w-16 h-16'>
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <svg
                      className='w-16 h-16 transform -rotate-90'
                      viewBox='0 0 64 64'
                    >
                      <circle
                        cx='32'
                        cy='32'
                        r='28'
                        stroke='#27272A'
                        strokeWidth='4'
                        fill='none'
                      />
                      <circle
                        cx='32'
                        cy='32'
                        r='28'
                        stroke={isCompleted ? '#FFD700' : '#A9D342'}
                        strokeWidth='4'
                        fill='none'
                        strokeDasharray={`${2 * Math.PI * 28}`}
                        strokeDashoffset={`${
                          2 * Math.PI * 28 * (1 - safeNextReward.progress / 100)
                        }`}
                        strokeLinecap='round'
                        className={isCompleted ? 'drop-shadow-lg' : ''}
                      />
                    </svg>
                    <div className='absolute inset-1 bg-card rounded-full flex items-center justify-center'>
                      <span
                        className={`text-sm font-medium ${
                          isCompleted ? 'text-yellow-400' : 'text-white'
                        }`}
                      >
                        {safeNextReward.progress}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Label className='text-sm text-gray-400 mt-[-15px]'>
              {isCompleted
                ? t('nextReward.congratulations')
                : t('nextReward.withdrawalGoal', {
                    amount: formatCurrency(safeNextReward.targetValue),
                  })}
            </Label>

            {isCompleted && (
              <div className='mt-4 p-3 bg-gradient-to-r from-yellow-600/20 to-gold-600/20 rounded-lg border border-yellow-500/30'>
                <p className='text-sm text-yellow-200 font-medium'>
                  {t('nextReward.valuableUser')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default React.memo(NextRewardCard);
